import { Box, Typography } from "@mui/material";
import SectionItem, { SectionItemProps } from "../../component/FaqSection";


const Faqs: React.FC = () => {

  const sectionItems: SectionItemProps[] = [
    { title: "General FAQ",
      content: [
        {
          question: "What is Soopra?",
          answer: "Soopra is an AI-driven platform that connects knowledgeable individuals with those seeking insights. Experts train AI personas to share their expertise, while fans ask questions and receive personalized, on-demand guidance."
        },
        {
          question: "How does Soopra work?",
          answer: "Experts create and refine AI personas based on their insights and expertise. Fans then browse these personas and ask questions. The AI persona responds instantly, drawing on the expert's knowledge to deliver accurate, authentic answers anytime, anywhere."
        },
        {
          question: "Is Soopra free to use?",
          answer: "Getting started is free for fans. You can explore a variety of AI personas and ask basic questions at no cost. Experts and fans can upgrade to premium features—such as in-depth sessions, exclusive Q&A courses, or advanced analytics—for a fee."
        },
        {
          question: "How do I sign up?",
          answer: `Simply visit our homepage and select "Sign Up Free." You can register using your email address or a supported single sign-on option. Once you're in, you can either create a persona (if you're an expert) or start exploring existing personas (if you're a fan).`
        },
      ]
    },
    { title: "For Experts",
      content: [
        {
          question: "Do I have to be a well-known author to create a persona?",
          answer: "Not at all. If you have valuable insights, specialized knowledge, or unique experiences you'd like to share, you can create an AI persona. Our platform is open to subject matter experts, educators, authors, consultants, coaches, and more."
        },
        {
          question: "How can I monetize my expertise on Soopra?",
          answer: "Experts can offer premium Q&A sessions, gated content, courses, or subscription models. You set your pricing and terms, and we handle the payment processing, so you can focus on delivering value and growing your reach."
        },
        {
          question: "Can I maintain my personal tone and style in my AI persona’s responses?",
          answer: `Yes. When you train your AI persona, you provide it with content, examples, and instructions that capture your voice and perspective. As it interacts with fans, it will use these guidelines to ensure responses feel genuinely "you."`
        },
        {
          question: "Do I get analytics or insights about user interactions?",
          answer: `Absolutely. Soopra provides analytics on popular questions, interaction frequency, and user feedback. These insights help you refine your persona's content and uncover new opportunities to meet user needs.`
        },
      ]
    },
    { title: "For Fans",
      content: [
        {
          question: "How do I find the right AI persona for my interests?",
          answer: `You can browse personas by category (e.g., business, health, education), read brief profiles, and even check featured Q&As to find the perfect match for your learning goals.`
        },
        {
          question: `Can I follow or “favorite” certain personas?`,
          answer: `Yes. By saving or subscribing to certain personas, you'll get quicker access to them, and if you opt into notifications, you'll be informed when new content, premium offerings, or updates become available.`
        },
        {
          question: "What if I need more detailed help or a private session?",
          answer: `Many experts offer premium or private sessions. You can purchase access to these special interactions for more in-depth guidance, step-by-step coaching, or personalized advice.`
        },
      ]
    },
    { title: "Privacy and Security",
      content: [
        {
          question: "Is my personal information secure?",
          answer: `Yes. We prioritize user privacy and use industry-standard security protocols. We never share your personal data without your consent.`
        },
        {
          question: "Are the conversations I have with an AI persona confidential?",
          answer: `Your interactions remain private. The expert receives aggregate insights to improve their persona's accuracy but does not see personal details or your specific identity.`
        },
        {
          question: "How accurate is the information I receive?",
          answer: `AI personas are trained on content provided by experts and refined over time. While we strive for accuracy, we recommend verifying critical information with reliable sources. If you find inaccuracies, please let us know so we can improve.`
        },
      ]
    },
  ]

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: {xs: 2.5, md: 5},
        py: {xs: 3, md: 4},
        maxWidth: "lg",
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '800px',
          gap: {xs: 3, md: 4}
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5">
            FAQs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Build trust and curiosity with product in action.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {
            sectionItems.map((section, ind) => (
              <SectionItem
                key={`${section.title}-${ind}`}
                title={section.title}
                content={section.content}
              />
            ))
          }
        </Box>
      </Box>
    </Box>
  )
}

export default Faqs;

